import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { listFavorites, deleteFavorite, listRegions } from "../lib/api";
import { Heart, Trash2, ArrowUpRight, RefreshCw } from "lucide-react";
import FavoriteEditor from "../components/FavoriteEditor";
import RegionPricingEditor from "../components/RegionPricingEditor";
import RegionStockEditor from "../components/RegionStockEditor";
import ColorVariantEditor from "../components/ColorVariantEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FavoritesPage() {
  const [favs, setFavs] = useState(null);
  const [regions, setRegions] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    listFavorites().then(setFavs).catch(() => setFavs([]));
  };

  useEffect(() => {
    load();
    listRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteFavorite(id);
      toast.success("Removed from favorites");
      load();
    } catch (e) {
      toast.error("Could not remove favorite");
    }
  };

  const handleSeedCatalogue = async () => {
    if (syncing) return;
    if (!window.confirm("Reset the storefront to the four-candle launch catalogue? This will wipe any existing products.")) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/seed-candle-catalog`, null, { timeout: 30000 });
      toast.success(`Seeded ${r.data.imported} candle products`, { description: `Removed ${r.data.removed} previous items.` });
      load();
    } catch (e) {
      toast.error("Seed failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="micro-label mb-3 flex items-center gap-2">
            <Heart size={14} strokeWidth={2.5} fill="#ef4444" className="text-red-500" /> CATALOGUE
          </div>
          <h1 className="text-5xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Products
          </h1>
          <p className="text-zinc-600 mt-2 max-w-xl text-sm">
            Manage your Smibha Luxe Candles catalogue here — edit, restock, or reset to the
            four-candle launch baseline.
          </p>
        </div>
        <button
          onClick={handleSeedCatalogue}
          disabled={syncing}
          className="inline-flex items-center gap-2 px-4 h-11 text-xs uppercase tracking-[0.2em] font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="seed-catalog-button"
          title="Reset the storefront to the four-candle launch catalogue"
        >
          <RefreshCw size={14} strokeWidth={2.5} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Seeding..." : "Reset to Candle Catalogue"}
        </button>
      </div>

      {favs === null && <div className="micro-label">LOADING...</div>}
      {favs?.length === 0 && (
        <div className="border border-zinc-200 p-16 text-center">
          <Heart size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} />
          <p className="text-zinc-600 mb-6">No favorites yet. Save winning products to review later.</p>
          <Link to="/admin" className="btn-klein inline-block text-xs">RUN RESEARCH</Link>
        </div>
      )}

      {favs?.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200" data-testid="favorites-grid">
          {favs.map((f, idx) => {
            const p = f.product;
            return (
              <div
                key={f.id}
                data-testid={`favorite-item-${idx}`}
                className="border-b border-zinc-200 lg:border-r last:border-r-0 flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-zinc-100 border-b border-zinc-200">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="micro-label mb-1 klein-text">
                    {p.niche === "home_decor" ? "HOME DECOR" : "GADGETS/TECH"}
                  </div>
                  <h3 className="text-lg font-black leading-tight mb-2" style={{ fontFamily: "Outfit" }}>
                    {p.name}
                  </h3>
                  <div className="flex gap-4 text-sm mb-4">
                    <div>
                      <div className="micro-label">Margin</div>
                      <div className="font-black klein-text" style={{ fontFamily: "Outfit" }}>
                        {p.profit_margin_pct}%
                      </div>
                    </div>
                    <div>
                      <div className="micro-label">Ship</div>
                      <div className="font-black" style={{ fontFamily: "Outfit" }}>
                        {p.shipping_days_to_ontario}d
                      </div>
                    </div>
                    <div>
                      <div className="micro-label">Sell</div>
                      <div className="font-black" style={{ fontFamily: "Outfit" }}>
                        ${p.selling_price_usd.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <FavoriteEditor
                    favorite={f}
                    rank={idx}
                    onUpdated={(updated) => {
                      setFavs((prev) =>
                        prev.map((x) => (x.id === updated.id ? updated : x))
                      );
                    }}
                  />

                  <RegionPricingEditor
                    favorite={f}
                    rank={idx}
                    regions={regions}
                    onUpdated={(updated) => {
                      setFavs((prev) =>
                        prev.map((x) => (x.id === updated.id ? updated : x))
                      );
                    }}
                  />

                  <RegionStockEditor
                    favorite={f}
                    rank={idx}
                    regions={regions}
                    onUpdated={(updated) => {
                      setFavs((prev) =>
                        prev.map((x) => (x.id === updated.id ? updated : x))
                      );
                    }}
                  />

                  <ColorVariantEditor
                    favorite={f}
                    rank={idx}
                    onUpdated={(updated) => {
                      setFavs((prev) =>
                        prev.map((x) => (x.id === updated.id ? updated : x))
                      );
                    }}
                  />

                  <div className="mt-4 flex items-center gap-2">
                    <a
                      href={p.supplier_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 btn-outline-dark text-[10px] inline-flex items-center justify-center gap-1"
                    >
                      CJ <ArrowUpRight size={12} strokeWidth={2.5} />
                    </a>
                    <button
                      onClick={() => handleDelete(f.id)}
                      data-testid={`delete-favorite-${idx}`}
                      className="w-10 h-10 border border-zinc-900 hover:bg-red-500 hover:text-white hover:border-red-500 flex items-center justify-center transition"
                      aria-label="Remove favorite"
                    >
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
